import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import EnhancedUserIdSection from './EnhancedUserIdSection';

const PrivacyStorageTab = ({ config, onUpdateConfig, isLoading }) => {
  const [formData, setFormData] = useState({
    gdprSettings: {
      cmpTimeout: 10000,
      defaultGdprScope: true,
      deviceAccess: true,
      allowAuctionWithoutConsent: false
    },
    userSync: {
      syncEnabled: true,
      syncDelay: 2500,
      auctionDelay: 300,
      syncsPerBidder: 5,
      filterSettings: {
        iframe: { bidders: '*', filter: 'include' },
        image: { bidders: '*', filter: 'include' }
      }
    },
    userIdModules: {}
  });
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  // Update form data when config changes
  useEffect(() => {
    if (config) {
      setFormData({
        gdprSettings: {
          cmpTimeout: config.gdprSettings?.cmpTimeout ?? 10000,
          defaultGdprScope: config.gdprSettings?.defaultGdprScope ?? true,
          deviceAccess: config.gdprSettings?.deviceAccess ?? true,
          allowAuctionWithoutConsent: config.gdprSettings?.allowAuctionWithoutConsent ?? false
        },
        userSync: {
          syncEnabled: config.userSync?.syncEnabled ?? true,
          syncDelay: config.userSync?.syncDelay ?? 2500,
          auctionDelay: config.userSync?.auctionDelay ?? 300,
          syncsPerBidder: config.userSync?.syncsPerBidder ?? 5,
          filterSettings: config.userSync?.filterSettings ?? {
            iframe: { bidders: '*', filter: 'include' },
            image: { bidders: '*', filter: 'include' }
          }
        },
        userIdModules: config.userIdModules || {}
      });
    }
  }, [config]);

  const handleGdprChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      gdprSettings: {
        ...prev.gdprSettings,
        [field]: value
      }
    }));
  };

  const handleUserSyncChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      userSync: {
        ...prev.userSync,
        [field]: value
      }
    }));
  };

  const handleFilterSettingsChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      userSync: {
        ...prev.userSync,
        filterSettings: {
          ...prev.userSync.filterSettings,
          [type]: {
            ...prev.userSync.filterSettings[type],
            [field]: value
          }
        }
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.gdprSettings.cmpTimeout < 100 || formData.gdprSettings.cmpTimeout > 50000) {
      newErrors.cmpTimeout = 'Timeout must be between 100 and 50000 ms';
    }
    
    if (formData.userSync.syncDelay < 0 || formData.userSync.syncDelay > 10000) {
      newErrors.syncDelay = 'Sync delay must be between 0 and 10000 ms';
    }
    
    if (formData.userSync.syncsPerBidder < 1 || formData.userSync.syncsPerBidder > 20) {
      newErrors.syncsPerBidder = 'Syncs per bidder must be between 1 and 20';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setMessage({ type: 'error', text: 'Please fix validation errors' });
      return;
    }
    
    setSaving(true);
    setMessage(null);
    
    try {
      const updatedConfig = {
        ...config,
        gdprSettings: formData.gdprSettings,
        userSync: formData.userSync,
        userIdModules: formData.userIdModules
      };
      
      // Remove old tcfControl if it exists
      delete updatedConfig.tcfControl;
      delete updatedConfig.tcf2Policy;
      
      await onUpdateConfig(updatedConfig);
      setMessage({ type: 'success', text: 'Privacy settings saved successfully' });
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      setMessage({ type: 'error', text: 'Failed to save privacy settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* GDPR Consent Management */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900">GDPR Consent Management</h2>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            Configure GDPR consent management settings for TCF 2.0 compliance using Prebid's standard consent management module.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CMP Timeout (ms)
              </label>
              <input
                type="number"
                value={formData.gdprSettings.cmpTimeout}
                onChange={(e) => handleGdprChange('cmpTimeout', parseInt(e.target.value))}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.cmpTimeout ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="10000"
              />
              {errors.cmpTimeout && (
                <p className="text-sm text-red-500 mt-1">{errors.cmpTimeout}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Time to wait for CMP to provide consent data
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default GDPR Scope
              </label>
              <select
                value={formData.gdprSettings.defaultGdprScope}
                onChange={(e) => handleGdprChange('defaultGdprScope', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="true">EU (GDPR applies)</option>
                <option value="false">Global (GDPR may not apply)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Default behavior when geolocation cannot be determined
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Device Access Control
              </label>
              <select
                value={formData.gdprSettings.deviceAccess}
                onChange={(e) => handleGdprChange('deviceAccess', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="true">Allow when consent is given</option>
                <option value="false">Always block cookies/localStorage</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Controls Prebid.js access to cookies and localStorage based on user consent
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allow Auction Without Consent
              </label>
              <select
                value={formData.gdprSettings.allowAuctionWithoutConsent}
                onChange={(e) => handleGdprChange('allowAuctionWithoutConsent', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="false">No - Wait for consent (recommended)</option>
                <option value="true">Yes - Allow auctions before consent</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                When set to No, prevents all auctions and bidder syncs until user consent is obtained
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* User Sync Configuration */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">User Sync Configuration</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Enable User Sync</label>
            <button
              type="button"
              onClick={() => handleUserSyncChange('syncEnabled', !formData.userSync.syncEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.userSync.syncEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.userSync.syncEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {formData.userSync.syncEnabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sync Delay (ms)
                  </label>
                  <input
                    type="number"
                    value={formData.userSync.syncDelay}
                    onChange={(e) => handleUserSyncChange('syncDelay', parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.syncDelay ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="2500"
                  />
                  {errors.syncDelay && (
                    <p className="text-sm text-red-500 mt-1">{errors.syncDelay}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auction Delay (ms)
                  </label>
                  <input
                    type="number"
                    value={formData.userSync.auctionDelay}
                    onChange={(e) => handleUserSyncChange('auctionDelay', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="300"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Syncs Per Bidder
                  </label>
                  <input
                    type="number"
                    value={formData.userSync.syncsPerBidder}
                    onChange={(e) => handleUserSyncChange('syncsPerBidder', parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.syncsPerBidder ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="5"
                  />
                  {errors.syncsPerBidder && (
                    <p className="text-sm text-red-500 mt-1">{errors.syncsPerBidder}</p>
                  )}
                </div>
              </div>
              
              {/* Filter Settings */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Sync Filter Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-md p-3">
                    <h4 className="text-xs font-medium text-gray-600 mb-2">iFrame Syncs</h4>
                    <div className="space-y-2">
                      <select
                        value={formData.userSync.filterSettings.iframe.filter}
                        onChange={(e) => handleFilterSettingsChange('iframe', 'filter', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="include">Include</option>
                        <option value="exclude">Exclude</option>
                      </select>
                      <input
                        type="text"
                        value={formData.userSync.filterSettings.iframe.bidders}
                        onChange={(e) => handleFilterSettingsChange('iframe', 'bidders', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="* for all, or comma-separated list"
                      />
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded-md p-3">
                    <h4 className="text-xs font-medium text-gray-600 mb-2">Image Syncs</h4>
                    <div className="space-y-2">
                      <select
                        value={formData.userSync.filterSettings.image.filter}
                        onChange={(e) => handleFilterSettingsChange('image', 'filter', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="include">Include</option>
                        <option value="exclude">Exclude</option>
                      </select>
                      <input
                        type="text"
                        value={formData.userSync.filterSettings.image.bidders}
                        onChange={(e) => handleFilterSettingsChange('image', 'bidders', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="* for all, or comma-separated list"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* User ID Modules */}
      <EnhancedUserIdSection 
        formData={formData}
        setFormData={setFormData}
      />

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || isLoading}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Privacy Settings
            </>
          )}
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-md flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}
    </div>
  );
};

export default PrivacyStorageTab;